import {
  Box,
  Icon,
  Image,
  Modal,
  ModalCloseButton,
  ModalContent,
  Theme,
  Typography,
  styled,
} from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import { faUserGroup } from "@outschool/icons";
import {
  Trans,
  renderCurrencyString,
  useLocale,
  useTranslation,
} from "@outschool/localization";
import { ExternalLink } from "@outschool/ui-components-shared";
import { useCopyLinkToClipboard } from "@outschool/ui-components-website";
import { Screen, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import giftImage from "../../../../images/invite-page/ReferralsProgram_2023.png";
import { CopyLinkToClipboard } from "../../CopyLinkToClipboard";
import { ShareButton, canShowNativeShare } from "../../ShareButton";
import { FacebookMessengerButton } from "../../sharing/FacebookMessengerButton";
import { FacebookShareButton } from "../../sharing/FacebookShareButton";
import waveImage from "./wave.svg";

const StyledInput = styled("input")(({ theme }) => ({
  background: "transparent",
  padding: 8,
  textAlign: "center",
  border: `1px solid ${theme.palette.neutral[300]}`,
  boxShadow: "inset 0 1px 2px rgba(102, 113, 123, 0.21)",
  borderRadius: "4px",
}));

const StyledButton = styled("button")(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.primary.main,
  padding: 8,
  border: 0,
  background: "none",
  cursor: "pointer",
  "&:hover:not(:disabled), &:focus:not(:disabled)": {
    color: theme.palette.primary.dark,
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  url: string;
  privateLink: string;
  referralCode: string | null;
};

export function ShareProfileWithReferralModal({
  open,
  onClose,
  url,
  privateLink,
  referralCode,
}: Props) {
  const Link = useLinkComponent();
  const { wasCopied, onCopy } = useCopyLinkToClipboard();
  const { t } = useTranslation(
    "client\\routes\\ParentProfile\\ParentProfileCard\\ShareProfileWithReferralModal",
  );
  const locale = useLocale();
  const isMobile = Screen.useIsMobile();

  const creditCashAmount = renderCurrencyString({
    priceInCents: REFER_CREDIT_CENTS,
    showCurrencyCode: false,
  });
  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "496px",
          p: 0,
          pt: 48,
        }}
      >
        <ModalCloseButton
          onClick={onClose}
          aria-label={t("Close")}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
          }}
        />
        <Box
          sx={{
            p: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            mb: 16,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Image src={giftImage} sx={{ height: 80 }} />
          </Box>
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            <Trans t={t}>
              Share your profile with a friend—
              <br />
              new users will get {{ creditCashAmount }} off!
            </Trans>
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 10 }}>
            <Trans t={t}>
              New users get a {{ creditCashAmount }} credit when they sign up
              using your referral link. When they take their first class, you’ll
              get {{ creditCashAmount }} too! <br />
              <ExternalLink
                url={
                  "https://support.outschool.com/" +
                  locale +
                  "/articles/2087332"
                }
                style={{
                  textDecoration: "underline",
                }}
              >
                Terms of use
              </ExternalLink>
            </Trans>
          </Typography>

          {isMobile ? (
            <CopyLinkToClipboard
              url={url}
              confirmationText={t("Link copied!")}
              trackingName="share_link_copy_button"
            >
              {t("Copy link")}
            </CopyLinkToClipboard>
          ) : (
            <div>
              <Typography variant="h6" sx={{ textAlign: "center" }}>
                {t("Share your profile link")}
              </Typography>
              <CopyLinkToClipboard
                url={url}
                confirmationText={t("Copied!")}
                trackingName="share_link_copy_button"
              >
                {t("Copy")}
              </CopyLinkToClipboard>
            </div>
          )}

          {canShowNativeShare() ? (
            <ShareButton
              url={url}
              text={t(
                "Check out Outschool! If you’re new to Outschool, you’ll get {{creditCashAmount}} referral credit with this link:",
                { creditCashAmount },
              )}
            >
              {t("Share link")}
            </ShareButton>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
              }}
            >
              <FacebookShareButton
                url={url}
                aria-label={t("Share your profile link to Facebook")}
                trackingName="share_via_facebook"
              >
                {t("Facebook")}
              </FacebookShareButton>
              <FacebookMessengerButton
                url={url}
                aria-label={t("Send your profile link on Facebook Messenger")}
                trackingName="share_via_messenger"
              >
                {t("Messenger")}
              </FacebookMessengerButton>
            </Box>
          )}
          <Link
            to={privateLink}
            target="_blank"
            style={{
              color: "theme.palette.primary.main",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: 16,
            }}
          >
            <Icon icon={faUserGroup} />
            {t("Preview my private profile link")}
          </Link>
        </Box>
        <Box
          sx={{
            p: 24,
            py: 42,
            position: "relative",
            backgroundColor: "neutral.100",
            ["&:before"]: {
              position: "absolute",
              top: "-17px",
              left: 0,
              height: "18px",
              width: "100%",
              content: `""`,
              display: "block",
              backgroundImage: `url("${waveImage}")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top center",
              backgroundSize: "100% 18px",
            },
          }}
        >
          <Typography variant="body2" as="h6" sx={{ fontWeight: 500 }}>
            {t("What’s shared with my profile?")}
          </Typography>
          <Typography variant="caption" as="p" sx={{ mb: 32 }}>
            {t(
              "Your profile contains your class enrollment history, teachers, favorites, and reviews.",
            )}
          </Typography>

          <Typography variant="body2" as="h6" sx={{ fontWeight: 500 }}>
            {t("What if my friend signs up with a different link?")}
          </Typography>
          <Typography variant="caption" as="p" sx={{ mb: 16 }}>
            {t(
              "No worries! If your friend already signed up, you both can still get credit if they add your referral code to their profile before taking their first class.",
            )}
          </Typography>

          {referralCode && (
            <>
              <Typography
                variant="caption"
                as="h6"
                sx={(theme: Theme) => ({
                  fontSize: theme.typography.pxToRem(12),
                  mb: 8,
                })}
              >
                {t("Your referral code")}
              </Typography>
              <Box sx={{ position: "relative" }}>
                <StyledInput
                  type="text"
                  value={referralCode}
                  readOnly
                  sx={{ width: "100%" }}
                />
                <StyledButton
                  onClick={() => onCopy(referralCode)}
                  sx={{ position: "absolute", right: 0 }}
                >
                  {wasCopied ? t("Copied!") : t("Copy")}
                </StyledButton>
              </Box>
            </>
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
}
